import * as graphql from '@treams/treams-network';

export function useViewerRoles(): {
	loading: boolean;
	roles: graphql.UserRole[];
	isManager: boolean;
	isManagerRole: (type: graphql.RoleType) => boolean;
	isDepartmentManager: boolean;
	isTeamManager: boolean;
	isGroupManager: boolean;
	isPrimaryGroupManager: boolean;
	isAdministrator: boolean;
	isLearningOfficer: boolean;
	isEngagementOfficer: boolean;
	isWorkspaceSuperuser: boolean;
	isWorkspaceAdmin: boolean;
	isHRBusinessPartner: boolean;
	canViewAndEditAllSettings: boolean;
} {
	const { data, loading } = graphql.useViewerRolesQuery({
		fetchPolicy: 'cache-first',
	});

	function hasRoleID(id: string) {
		return data?.viewer.roles.some((role) => role.role.id === id) ?? false;
	}

	function hasRoleType(type: graphql.RoleType) {
		return data?.viewer.roles.some((role) => role.role.type === type) ?? false;
	}

	function hasRoleTargetType(targetType: graphql.RoleTargetType, type: graphql.RoleType) {
		return (
			data?.viewer.roles.some(
				(role) => role.target.type === targetType && role.role.type === type,
			) ?? false
		);
	}

	const isWorkspaceSuperuser = hasRoleID('workspace-super-user');
	const isAdministrator = hasRoleID('workspace-administrator');
	const isLearningOfficer = hasRoleID('workspace-learning-officer');
	const isEngagementOfficer = hasRoleID('workspace-engagement-officer');
	const isWorkspaceAdmin = hasRoleID('workspace-admin');
	const isHRBusinessPartner = hasRoleID('workspace-hr-business-partner');

	const managerRoleTypes: graphql.RoleType[] = [
		graphql.RoleType.PrimaryManager,
		graphql.RoleType.CollaborativeManager,
		graphql.RoleType.Coach,
		graphql.RoleType.Owner,
	];

	function isManagerRole(type: graphql.RoleType) {
		return managerRoleTypes.includes(type);
	}

	const isManager = managerRoleTypes.some((type) => hasRoleType(type));

	const isDepartmentManager = [
		hasRoleTargetType(graphql.RoleTargetType.Department, graphql.RoleType.PrimaryManager),
		hasRoleTargetType(graphql.RoleTargetType.Department, graphql.RoleType.CollaborativeManager),
		hasRoleTargetType(graphql.RoleTargetType.Department, graphql.RoleType.Coach),
	].some(Boolean);

	const isTeamManager = hasRoleTargetType(graphql.RoleTargetType.Team, graphql.RoleType.Owner);

	const isGroupManager = isWorkspaceSuperuser || isDepartmentManager || isTeamManager;

	// Mirrors the backend analytics permissions: a group is only accessible to the primary
	// manager of a department or the owner of a team. Coaches, buddies and collaborative
	// managers are deliberately excluded, as are managers of individual users.
	const isPrimaryGroupManager =
		isWorkspaceSuperuser ||
		hasRoleTargetType(graphql.RoleTargetType.Department, graphql.RoleType.PrimaryManager) ||
		isTeamManager;

	const canViewAndEditAllSettings =
		isWorkspaceSuperuser || isWorkspaceAdmin || isAdministrator || isHRBusinessPartner;

	return {
		loading,
		roles: (data?.viewer.roles as graphql.UserRole[]) ?? [],
		isManager,
		isManagerRole,
		isDepartmentManager,
		isTeamManager,
		isGroupManager,
		isPrimaryGroupManager,
		isAdministrator,
		isLearningOfficer,
		isEngagementOfficer,
		isWorkspaceSuperuser,
		isWorkspaceAdmin,
		isHRBusinessPartner,
		canViewAndEditAllSettings,
	};
}
